import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicForm } from '../lib/api';
import FormRenderer from '../components/renderer/FormRenderer';
import type { Form } from '../types/form';

export default function PublicFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    if (!slug) return;

    async function loadForm() {
      try {
        setLoading(true);
        const formData = await getPublicForm(slug!);
        setForm(formData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Form not found');
      } finally {
        setLoading(false);
      }
    }

    loadForm();
  }, [slug]);

  function handleSuccess(result: { submissionId: string; message: string; redirectUrl?: string }) {
    setSubmitted(true);
    setSuccessMessage(result.message);
  }

  function handleError(err: Error) {
    setError(err.message);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Form Not Found</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const isDark = form?.schema.settings.theme === 'dark';

    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <div className={`text-center max-w-md p-8 rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Submission Received!
          </h1>
          <p className={isDark ? 'text-gray-300' : 'text-gray-500'}>
            {successMessage}
          </p>
        </div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  const isDark = form.schema.settings.theme === 'dark';

  return (
    <div className={`min-h-screen py-12 px-4 ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
      <div className="max-w-xl mx-auto">
        {/* Form Header */}
        <div className="text-center mb-8">
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {form.name}
          </h1>
          {form.description && (
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {form.description}
            </p>
          )}
        </div>

        {/* Form */}
        <FormRenderer
          formId={form.id}
          schema={form.schema}
          onSuccess={handleSuccess}
          onError={handleError}
        />

        {/* Footer */}
        <div className="text-center mt-8">
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Powered by Form Builder
          </p>
        </div>
      </div>
    </div>
  );
}
